import React, {useCallback, useEffect, useMemo, useState} from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Pagination, TextField } from "@mui/material";
import {
    Header,
} from "./GroupList.styled";
import axios from "axios";
import {ReactComponent as UsersPlusIcon} from "../../assets/icons/users-plus.svg";
import {formatDate} from "../../utils/format-date";
import {useNavigate} from "react-router-dom";
import {ReactComponent as TrashIcon} from "../../assets/icons/trash.svg";
import {ReactComponent as ArrowCircleRight} from "../../assets/icons/arrow-circle-right.svg";
import ConfirmationDialog from "../../components/dialogs/ConfirmationDialog";
import {ActionButton, CancelButton} from "../../components/common/ActionButtons";
import {parseError} from "../../services/http.service";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {useTranslation} from 'react-i18next';
import SearchWithTagsInput from "../../components/common/SearchWithTagsInput";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import ResponsiveTable, { ResponsiveTableColumn } from "../../components/common/ResponsiveTable";
import {
    ControlsRow,
    PageSizeWrapper, PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../components/common/TableContainer.styled";
import FieldHelpSuffix from "../../components/common/FieldHelpSuffix";

interface Group {
    id: number;
    name: string;
    createdAt: string;
}

export async function fetchGroups(page = 0, size = 10, searchTags: string[]) {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("size", size.toString());
    searchTags.forEach(tag => params.append('groupPartName', tag));

    const response = await axios.get("/groups", {params});
    return {
        content: (response.data?.content ?? []) as Group[],
        totalElements: response.data.totalElements || 0,
    };
}

export async function removeGroupForId(id: number) {
    return await axios.delete(`/groups/${id}`);
}

const GroupList: React.FC = () => {
    const [groups, setGroups] = useState<Group[]>([]);
    const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false);
    const [groupName, setGroupName] = useState("");
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
    const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
    const {showSnackbar} = useSnackbar();
    const [totalElements, setTotalElements] = useState(0);
    const pageSizeOptions = [5, 10, 20, 50];
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTags, setSearchTags] = useState<string[]>([]);

    const {t} = useTranslation();

    const fetchData = useCallback(async () => {
        try {
            const fetchedGroups = await fetchGroups(currentPage - 1, pageSize, searchTags);
            setGroups(fetchedGroups.content);
            setTotalElements(fetchedGroups.totalElements);
        } catch (error) {
            showSnackbar(parseError(error), "error");
            setGroups([]);
            setTotalElements(0);
        }
    }, [currentPage, pageSize, searchTags, showSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!isCreateGroupDialogOpen) {
            setGroupName("");
            setError("");
        }
    }, [isCreateGroupDialogOpen]);

    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            setError(t('groupList.createDialog.error') as string);
            return;
        }
        try {
            await axios.post("/groups", {groupName});
            setIsCreateGroupDialogOpen(false);
            await fetchData();
            setGroupName("");
            setError("");
        } catch (error) {
            setError(parseError(error));
        }
    };

    const navigateToGroupDetails = useCallback((id: number, name: string) => {
        navigate(`/admin/group/${id}?name=${name}`)
    }, [navigate]);

    const openDeleteDialog = useCallback((group: Group) => {
        setGroupToDelete(group);
        setIsDeleteConfirmDialogOpen(true);
    }, []);

    const confirmRemoveGroup = async () => {
        if (!groupToDelete) return;
        try {
            const response = await removeGroupForId(groupToDelete.id);
            if (response) {
                await fetchData();
            }
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            setIsDeleteConfirmDialogOpen(false);
            setGroupToDelete(null);
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleTagsChange = (tags: string[]) => {
        setSearchTags(tags);
        setCurrentPage(1);
    };

    const columns: ResponsiveTableColumn<Group>[] = useMemo(() => [
        {
            id: 'name',
            header: t('groupList.tableHeaders.name'),
            render: (g) => g.name,
        },
        {
            id: 'createdAt',
            header: t('groupList.tableHeaders.createdAt'),
            render: (g) => formatDate(g.createdAt),
        },
        {
            id: 'actions',
            header: t('groupList.tableHeaders.actions'),
            cellClassName: 'actions-cell',
            render: (g) => (
                <>
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={() => openDeleteDialog(g)}
                        aria-label={t('groupList.deleteButton') as string}
                    >
                        <TrashIcon aria-hidden={true} focusable={false}/>
                        <span style={{marginLeft: '8px'}}>{t('groupList.deleteButton')}</span>
                    </Button>
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={() => navigateToGroupDetails(g.id, g.name)}
                        aria-label={t('groupList.viewDetailsButton') as string}
                    >
                        <ArrowCircleRight aria-hidden={true} focusable={false}/>
                        <span style={{marginLeft: '8px'}}>{t('groupList.viewDetailsButton')}</span>
                    </Button>
                </>
            ),
        },
    ], [t, openDeleteDialog, navigateToGroupDetails]);

    return (
        <StyledTableContainer role="region" aria-labelledby="group-list-title">
                <Header>
                    <h2 id="group-list-title">{t('groupList.title')}</h2>
                    <Button
                        startIcon={<UsersPlusIcon aria-hidden={true} focusable={false}/>} 
                        variant="contained" color="primary" onClick={() => setIsCreateGroupDialogOpen(true)}
                        aria-label={t('groupList.createButton') as string}
                    >
                        {t('groupList.createButton')}
                    </Button>
                </Header>

                <ControlsRow>
                    <SearchWrapper>
                        <SearchWithTagsInput onTagsChange={handleTagsChange} initialTags={searchTags} ariaLabel={t('groupList.search.ariaLabel') as string}/>
                    </SearchWrapper>
                    <PageSizeWrapper>
                        <PageSizeSelector
                            options={pageSizeOptions}
                            selectedSize={pageSize}
                            onChange={handlePageSizeChange}
                            ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                            getSizeAriaLabel={(size) => t('pageSizeSelector.aria.setSize', { size }) as string}
                        />
                    </PageSizeWrapper>
                </ControlsRow>

                <ResponsiveTable
                    columns={columns}
                    data={groups}
                    getRowKey={(g) => g.id}
                    emptyState={t('groupList.emptyState')}
                    ariaLabel={t('groupList.table.ariaLabel') as string}
                />
                {totalElements > pageSize && (
                    <PaginationWrapper>
                        <Pagination
                            count={Math.ceil(totalElements / pageSize)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            aria-label={t('groupList.pagination.ariaLabel') as string}
                        />
                    </PaginationWrapper>
                )}
            <Dialog
                open={isCreateGroupDialogOpen}
                onClose={() => setIsCreateGroupDialogOpen(false)}
                fullWidth
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 'auto' },
                        m: { xs: 1.5, sm: '24px' },
                    }
                }}
            >
                <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 0 }}>
                    {t('groupList.createDialog.title')}
                </DialogTitle>
                <DialogContent sx={{ padding: { xs: '16px 20px !important', sm: '32px 64px !important' }, width: { xs: '100%', sm: '400px' } }}>
                    <TextField
                        label={t('groupList.createDialog.groupNameLabel')}
                        fullWidth
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        error={!!error}
                        helperText={error || ' '}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('groupList.createDialog.help.groupName')}
                                    ariaLabel={t('groupList.createDialog.help.groupName') as string}
                                />
                            ),
                        }}
                        sx={{ mt: { xs: 1, sm: 0 } }}
                    />
                </DialogContent>
                <DialogActions
                    sx={{
                        paddingBottom: { xs: '16px', sm: '20px' },
                        paddingRight: { xs: '16px', sm: '20px' },
                        paddingLeft: { xs: '16px', sm: '20px' },
                        justifyContent: { xs: 'stretch', sm: 'space-between' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 },
                    }}
                >
                    <CancelButton
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                        onClick={() => {
                            setIsCreateGroupDialogOpen(false)
                        }}
                    >
                        {t('groupList.cancelButton')}
                    </CancelButton>
                    <ActionButton sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={handleCreateGroup}>
                        {t('groupList.createButton')}
                    </ActionButton>
                </DialogActions>
            </Dialog>
            {groupToDelete && (
                <ConfirmationDialog
                    open={isDeleteConfirmDialogOpen}
                    onClose={() => setIsDeleteConfirmDialogOpen(false)}
                    onConfirm={confirmRemoveGroup}
                    title={t('groupList.deleteDialog.title')}
                    message={<>Czy na pewno chcesz usunąć grupę: &ldquo;{groupToDelete.name}&rdquo;?</>}
                    confirmButtonText={t('groupList.deleteButton') as string}
                    cancelButtonText={t('groupList.cancelButton') as string}
                />
            )}
        </StyledTableContainer>
    );
};

export default GroupList;
